// Bonus pick card with holographic gold/rainbow aura and confetti burst on flip.
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Lock, Sparkles, Maximize2, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { RiskBadge } from "@/components/RiskBadge";
import { Button } from "@/components/ui/button";
import { ConfettiBurst } from "@/components/ConfettiBurst";
import { playFlipSound, playDingSound } from "@/lib/sound";

export const BonusPickCard = ({ slot, isLoggedIn }) => {
    const [flipped, setFlipped] = useState(false);
    const [confettiTrigger, setConfettiTrigger] = useState(0);
    const locked = !slot.unlocked;
    const flippedOnceRef = useRef(false);

    useEffect(() => { setFlipped(false); }, [slot.image, slot.unlocked]);

    const onToggle = () => {
        if (locked) return;
        playFlipSound();
        setFlipped((f) => {
            const next = !f;
            if (next && !flippedOnceRef.current) {
                setConfettiTrigger((c) => c + 1);
                playDingSound();
                flippedOnceRef.current = true;
            }
            return next;
        });
    };

    const onKey = (e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); }
    };

    return (
        <div className="relative w-full" style={{ perspective: "1400px" }} data-testid="bonus-pick-card" data-locked={locked ? "true" : "false"}>
            <motion.div
                role="button"
                aria-label={`Bonus Pick. ${locked ? "Locked. Subscribe to unlock." : flipped ? "Showing pick." : "Click to reveal."}`}
                tabIndex={0}
                onClick={onToggle}
                onKeyDown={onKey}
                data-testid="bonus-pick-card-toggle"
                className="relative w-full h-[320px] sm:h-[360px] rounded-2xl pp-card-bg cursor-pointer select-none outline-none focus-visible:ring-2 focus-visible:ring-[#facc15] overflow-hidden"
                style={{
                    transformStyle: "preserve-3d",
                    boxShadow: "0 0 0 1px rgba(250, 204, 21, 0.5), 0 0 40px rgba(250, 204, 21, 0.4), 0 0 80px rgba(168, 85, 247, 0.3)",
                }}
                animate={{ rotateY: flipped ? 180 : 0 }}
                transition={{ duration: 0.95, ease: [0.22, 1, 0.36, 1] }}
            >
                {/* Holographic shimmer overlay */}
                <motion.div
                    aria-hidden
                    className="absolute inset-0 rounded-2xl pointer-events-none"
                    style={{
                        background: "linear-gradient(120deg, rgba(34,211,238,0.20), rgba(168,85,247,0.20), rgba(250,204,21,0.20), rgba(239,68,68,0.20), rgba(34,211,238,0.20))",
                        backgroundSize: "400% 400%",
                        mixBlendMode: "overlay",
                    }}
                    animate={{ backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"] }}
                    transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
                />

                {/* FRONT */}
                <div className="absolute inset-0 rounded-2xl p-5 flex flex-col justify-between" style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }}>
                    <div className="flex items-start justify-between">
                        <div>
                            <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md" style={{ background: "rgba(250,204,21,0.15)", border: "1px solid rgba(250,204,21,0.55)" }}>
                                <Star className="w-3.5 h-3.5" style={{ color: "#facc15" }} />
                                <span className="text-xs font-mono uppercase tracking-widest" style={{ color: "#facc15" }}>BONUS</span>
                            </div>
                            <p className="mt-2 font-display text-3xl tracking-widest text-white neon-text-purple">HOLO PICK</p>
                            <p className="mt-1 text-xs text-[#b7b7c6]">Pro & Premium exclusive</p>
                        </div>
                        <RiskBadge risk={slot.risk} />
                    </div>

                    <div className="flex flex-col items-center text-center">
                        <motion.div
                            className="w-20 h-20 rounded-full pp-bg-2 flex items-center justify-center border-2 mb-3"
                            style={{ borderColor: "#facc15" }}
                            animate={{ rotate: [0, 360], boxShadow: ["0 0 20px #facc15aa", "0 0 40px #a855f7aa", "0 0 20px #22d3eeaa", "0 0 40px #facc15aa"] }}
                            transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
                        >
                            <Sparkles className="w-9 h-9" style={{ color: "#facc15" }} />
                        </motion.div>
                        <p className="font-display text-2xl tracking-widest text-white">{slot.has_image ? "TAP TO REVEAL" : "DROPPING SOON"}</p>
                        <p className="mt-1 text-xs text-[#b7b7c6]">{slot.has_image ? "Owner’s exclusive bonus slip" : "Bonus drops with daily picks"}</p>
                    </div>

                    <div className="flex items-center justify-between text-xs text-[#b7b7c6]">
                        <span className="font-mono uppercase">HOTSLIP</span>
                        <span className="font-mono">EXCLUSIVE</span>
                    </div>
                </div>

                {/* BACK */}
                <div className="absolute inset-0 rounded-2xl p-3" style={{ backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden", transform: "rotateY(180deg)" }}>
                    <div className="relative w-full h-full rounded-xl pp-bg-2 overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between p-2">
                            <RiskBadge risk={slot.risk} />
                            {slot.has_image && slot.image && (
                                <Dialog>
                                    <DialogTrigger asChild>
                                        <Button onClick={(e) => e.stopPropagation()} variant="ghost" size="icon" className="text-[#b7b7c6] hover:text-white" data-testid="bonus-pick-zoom-button">
                                            <Maximize2 className="w-4 h-4" />
                                        </Button>
                                    </DialogTrigger>
                                    <DialogContent className="pp-bg-0 border-[#242436] text-white max-w-3xl">
                                        <DialogHeader><DialogTitle className="font-display tracking-widest">BONUS PICK</DialogTitle></DialogHeader>
                                        <img src={slot.image} alt="Bonus pick slip" className="w-full rounded-lg" data-testid="bonus-pick-image-zoomed" />
                                        {slot.title && <p className="mt-2 font-medium text-white">{slot.title}</p>}
                                        {slot.description && <p className="text-sm text-[#b7b7c6]">{slot.description}</p>}
                                    </DialogContent>
                                </Dialog>
                            )}
                        </div>
                        <div className="flex-1 flex items-center justify-center p-1">
                            {slot.image ? (
                                <img src={slot.image} alt="Bonus pick slip" className="max-w-full max-h-full object-contain rounded-md" data-testid="bonus-pick-image" />
                            ) : (
                                <div className="text-sm text-[#8b8ba0]">No bonus image yet — check back soon.</div>
                            )}
                        </div>
                        {slot.title && <div className="px-3 py-2 text-xs text-[#b7b7c6] border-t border-[#242436]">{slot.title}</div>}
                    </div>
                </div>
            </motion.div>

            {/* LOCKED OVERLAY */}
            {locked && (
                <div
                    className="absolute inset-0 rounded-2xl pp-bg-0 border flex flex-col items-center justify-center p-6 text-center"
                    style={{ borderColor: "#facc15", boxShadow: "0 0 0 1px rgba(250,204,21,0.4), 0 0 36px rgba(168,85,247,0.35)" }}
                    data-testid="bonus-pick-locked-overlay"
                >
                    <motion.div
                        className="w-16 h-16 rounded-full pp-bg-1 border flex items-center justify-center mb-3"
                        style={{ borderColor: "#facc15" }}
                        animate={{ boxShadow: ["0 0 20px #facc15aa", "0 0 40px #a855f7aa", "0 0 20px #facc15aa"] }}
                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                    >
                        <Lock className="w-7 h-7" style={{ color: "#facc15" }} />
                    </motion.div>
                    <p className="font-display text-2xl tracking-widest text-white">BONUS PICK</p>
                    <p className="mt-1 text-sm text-[#b7b7c6]">Holo exclusive — Pro & Premium only</p>
                    <Button asChild className="mt-4 text-[#07130b] font-semibold" style={{ background: "linear-gradient(90deg, #facc15, #a855f7)" }} data-testid="bonus-pick-locked-overlay-cta">
                        <Link to={isLoggedIn ? "/pricing" : "/signup"}>{isLoggedIn ? "Subscribe to unlock" : "Sign up to unlock"}</Link>
                    </Button>
                </div>
            )}
            <ConfettiBurst trigger={confettiTrigger} count={80} />
        </div>
    );
};
